import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  Space,
  Table,
  Button,
  Typography,
  Row,
  Col,
  Card,
  Tooltip,
  InputNumber,
  notification,
  Checkbox,
  Select,
} from 'antd';
import './Players.css';

import {
  AUTO_TRADING,
  fetchAutoTraderData,
  fetchPlayers,
  FINISHED,
  HISTORICAL_SCENARIO,
  REAL_TIME_SCENARIO,
  retrieveUnderlyingAssets,
  selectors,
  updateAutoDealsPerPeriod,
  updateAutoODA,
  updateAutoTradedPlayers,
  updateAutoTradeOnlyInRepCcy,
  updateConcurrentDeals,
  updateHistoricalStep,
  updateHistoricalNewsDelivery,
  updateStopIfDiffs,
  toggleShowAnalysisTab,
  toggleShowAnalysisStudioTab,
  toggleShowBiasAndReportInPlayerView,
  updateNewsGroupingMode,
} from 'state';
import { LoadingScreen } from 'components';

const Players: React.FC = () => {
  const dispatch = useDispatch();
  const { session_id } = useParams<{ session_id: string }>();
  const sessionId = Number(session_id);

  const session = useSelector(selectors.sessionById(sessionId));
  const players = useSelector(selectors.playersListBySessionId(sessionId));
  const underlyingAssets = useSelector(selectors.underlyingAssetsList);

  const players_fetched = session?.players_fetched;
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedDealsPerPeriod, setDealsPerPeriod] = useState<
    number | undefined
  >(session.auto_deals_per_period);
  const [timePerPeriod, setTimePerPeriod] = useState<number | undefined>(
    session.historical_sleep_time
  );
  const [staggeredNewsEnabled, setStaggeredNewsEnabled] = useState<boolean>(
    session.historical_news_delivery_enabled ?? false,
  );
  const [newsPushInterval, setNewsPushInterval] = useState<number | undefined>(
    session.historical_news_push_interval_seconds ?? 30,
  );
  const [newsQuietSeconds, setNewsQuietSeconds] = useState<number | undefined>(
    session.historical_news_quiet_seconds ?? 0,
  );
  const [concurrentDeals, setConcurrentDeals] = useState<boolean>(
    session.concurrent_deals
  );
  const [stopIfDiffs, setStopIfDiffs] = useState<boolean>(
    session.stop_if_differences
  );
  const [autoTradeOnlyInRepCcy, setAutoTradeOnlyInRepCcy] = useState<boolean>(
    session.auto_trade_only_in_reporting_ccy
  );
  const [autoODA, setAutoODA] = useState<boolean>(session.auto_oda);

  const isRealTimeUnfinished =
    session.scenario_type === REAL_TIME_SCENARIO && session.status !== FINISHED;

  const disabled =
    session.auto_trader_status === AUTO_TRADING || isRealTimeUnfinished;
  const tooltipMessage =
    session.auto_trader_status === AUTO_TRADING
      ? 'Cannot change which players are auto-traded while auto-trading in progress.'
      : 'Cannot use auto-trader to progress a real time unfinished session up until current date since the session itself is up to current date.';
  const analysisTabToggleDisabled =
    session.scenario_type !== HISTORICAL_SCENARIO;
  const analysisTabTooltip = analysisTabToggleDisabled
    ? 'Analysis tab is only available for historical sessions.'
    : '';
  // Legacy Analysis tab is retired — keep the toggle code but never render it.
  const showLegacyAnalysisTabToggle = false;
  const newsGroupingOptions: Array<{
    value: 'all' | 'geography' | 'sector';
    label: string;
  }> = [
    { value: 'all', label: 'All' },
    { value: 'geography', label: 'Geography' },
    { value: 'sector', label: 'Sector' },
  ];

  useEffect(() => {
    dispatch(retrieveUnderlyingAssets({}));
    dispatch(fetchAutoTraderData({ session_id: sessionId }));
  }, [dispatch]);

  useEffect(() => {
    if (session && session.auto_traded_players) {
      setSelectedRowKeys(session.auto_traded_players);
    }
  }, [session]);

  useEffect(() => {
    setStaggeredNewsEnabled(session.historical_news_delivery_enabled ?? false);
    setNewsPushInterval(session.historical_news_push_interval_seconds ?? 30);
    setNewsQuietSeconds(session.historical_news_quiet_seconds ?? 0);
  }, [
    session.historical_news_delivery_enabled,
    session.historical_news_push_interval_seconds,
    session.historical_news_quiet_seconds,
  ]);

  useEffect(() => {
    if (loading) return;
    if (!session) return;
    if (players_fetched) return;

    dispatch(fetchPlayers({ session_id: sessionId }));
    setLoading(true);
  }, [dispatch, loading, players_fetched, session, sessionId]);

  if (!session) {
    return (
      <Typography.Paragraph>
        Session {sessionId} not found!
      </Typography.Paragraph>
    );
  }

  if (!players.length || !underlyingAssets.length) {
    return <LoadingScreen />;
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  return (
    <div>
      <Row gutter={60}>
        <Col span={8}>
          <Table
            rowKey="id"
            bordered
            dataSource={players}
            pagination={false}
            rowSelection={rowSelection}
            columns={[
              {
                title: 'Id',
                dataIndex: 'id',
                key: 'id',
                sorter: (a, b) => a.id - b.id,
                width: '50px',
              },
              {
                title: 'Name',
                dataIndex: 'name',
                width: '200px',
              },
              {
                title: 'Action',
                key: 'action',
                render: function Action(text, record) {
                  return (
                    <Space key={record.id} size="middle">
                      <Link
                        to={`/dashboard/sessions/${session_id}/players/${record.id}`}
                      >
                        <Button>Select</Button>
                      </Link>
                      <Link
                        to={`/dashboard/sessions/${session_id}/players/${record.id}/individual-report`}
                      >
                        <Button>Indv. Report</Button>
                      </Link>
                    </Space>
                  );
                },
              },
            ]}
          />
        </Col>

        <Col
          span={8}
          style={{
            borderLeft: '2px solid #eeeeee',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Row>
            <Card title={'New Auto-Trader Menu'}>
              <div className="autoTraderMenuRow">
                <div className="autoTraderMenuLabel">Auto-traded players:</div>
                <div className="autoTraderMenuValue">
                  {session.auto_traded_players &&
                  session.auto_traded_players.length !== 0 ? (
                    <div style={{ display: 'inline-block' }}>
                      {/*  {session.auto_traded_players.map(player_id: number => {*/}
                      {/*    return players.find(item => item.id === player_id);*/}
                      {/*  })*/}
                      {/*}*/}
                      {session.auto_traded_players.map(player_id => {
                        const player = players.find(
                          player => player.id === player_id
                        );
                        if (player) return `${player.name}, `;
                      })}
                      {/*tpl1, tpl2*/}
                    </div>
                  ) : (
                    <div style={{ display: 'inline-block' }}>
                      No players selected, tick them using the table and save.
                    </div>
                  )}
                  <Tooltip title={disabled ? tooltipMessage : ''}>
                    <span>
                      <Button
                        size="middle"
                        disabled={disabled}
                        style={{ marginLeft: 10 }}
                        onClick={() =>
                          dispatch(
                            updateAutoTradedPlayers({
                              session_id: sessionId,
                              player_ids: selectedRowKeys.map(key =>
                                Number(key)
                              ),
                            })
                          )
                        }
                      >
                        Save
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </div>
              <div className="autoTraderMenuRow">
                <div className="autoTraderMenuLabel">Deals per period:</div>
                <div className="autoTraderMenuValue">
                  <InputNumber
                    value={selectedDealsPerPeriod}
                    onChange={value => setDealsPerPeriod(value)}
                    min={1}
                  />
                  <span>
                    <Button
                      size="middle"
                      disabled={disabled}
                      style={{ marginLeft: 10 }}
                      onClick={() => {
                        if (selectedDealsPerPeriod !== undefined) {
                          dispatch(
                            updateAutoDealsPerPeriod({
                              session_id: sessionId,
                              deals_per_period: selectedDealsPerPeriod,
                            })
                          );
                        } else {
                          notification.error({
                            description: '',
                            message: 'Invalid input',
                          });
                        }
                      }}
                    >
                      Save
                    </Button>
                  </span>
                </div>
              </div>
              <div className="autoTraderMenuRow">
                <div className="autoTraderMenuLabel">Time per period:</div>
                <div className="autoTraderMenuValue">
                  <InputNumber
                    value={timePerPeriod}
                    onChange={value => setTimePerPeriod(value)}
                    min={1}
                  />
                  <span>
                    <Button
                      size="middle"
                      disabled={disabled}
                      style={{ marginLeft: 10 }}
                      onClick={() => {
                        if (timePerPeriod !== undefined) {
                          dispatch(
                            updateHistoricalStep({
                              session_id: session.id,
                              sleep_time: timePerPeriod,
                              admin: true,
                            })
                          );
                        } else {
                          notification.error({
                            description: '',
                            message: 'Invalid input',
                          });
                        }
                      }}
                    >
                      Save
                    </Button>
                  </span>
                </div>
              </div>
              <div className="autoTraderMenuRow">
                <div className="autoTraderMenuLabel">Concurrent deals:</div>
                <div className="autoTraderMenuValue">
                  <Checkbox
                    checked={concurrentDeals}
                    onChange={e => {
                      console.log(e.target.checked);

                      dispatch(
                        updateConcurrentDeals({
                          session_id: session.id,
                          concurrent_deals: e.target.checked,
                        })
                      );
                      setConcurrentDeals(e.target.checked);
                    }}
                  />
                </div>
              </div>
              <div className="autoTraderMenuRow">
                <div className="autoTraderMenuLabel">Stop if diffs:</div>
                <div className="autoTraderMenuValue">
                  <Checkbox
                    checked={stopIfDiffs}
                    onChange={e => {
                      console.log(e.target.checked);

                      dispatch(
                        updateStopIfDiffs({
                          session_id: session.id,
                          stop_if_differences: e.target.checked,
                        })
                      );
                      setStopIfDiffs(e.target.checked);
                    }}
                  />
                </div>
              </div>
              <div className="autoTraderMenuRow">
                <div className="autoTraderMenuLabel">
                  Only Trade in rep ccy:
                </div>
                <div className="autoTraderMenuValue">
                  <Checkbox
                    checked={autoTradeOnlyInRepCcy}
                    onChange={e => {
                      console.log(e.target.checked);

                      dispatch(
                        updateAutoTradeOnlyInRepCcy({
                          session_id: session.id,
                          auto_trade_only_in_reporting_ccy: e.target.checked,
                        })
                      );
                      setAutoTradeOnlyInRepCcy(e.target.checked);
                    }}
                  />
                </div>
              </div>
              <div className="autoTraderMenuRow">
                <div className="autoTraderMenuLabel">Auto ODA:</div>
                <div className="autoTraderMenuValue">
                  <Checkbox
                    checked={autoODA}
                    onChange={e => {
                      console.log(e.target.checked);

                      dispatch(
                        updateAutoODA({
                          session_id: session.id,
                          auto_oda: e.target.checked,
                        })
                      );
                      setAutoODA(e.target.checked);
                    }}
                  />
                </div>
              </div>
            </Card>
          </Row>
          <br />
          {/*<Row>*/}
          {/*  <Card title="Old Auto-Trader Menu" style={{ minWidth: '660px' }}>*/}
          {/*    {isRealTimeUnfinished ? (*/}
          {/*      <div>*/}
          {/*        Cannot progress an unfinished real time session since its*/}
          {/*        scenario date is already at current date.*/}
          {/*      </div>*/}
          {/*    ) : (*/}
          {/*      <div>*/}
          {/*        <div>*/}
          {/*          <WarningOutlined style={{ marginRight: '10px' }} />*/}
          {/*          Progressing a session with Auto-Trader will convert any type*/}
          {/*          of session into a Historical session.*/}
          {/*        </div>*/}
          {/*        <div>*/}
          {/*          <WarningOutlined style={{ marginRight: '10px' }} />*/}
          {/*          Converting and preparing a session for Auto-Trader will*/}
          {/*          rewrite PlayerHistory and Metrics datapoint timestamps.*/}
          {/*          Consider cloning desired session and running Auto-Trader on*/}
          {/*          the clone.*/}
          {/*        </div>*/}
          {/*        <div>*/}
          {/*          <WarningOutlined style={{ marginRight: '10px' }} />*/}
          {/*          Not all essential data will be updated on socket as periods*/}
          {/*          change (cash accounts, allocations, blotter). Not playable*/}
          {/*          by real users.*/}
          {/*        </div>*/}
          {/*        <div>*/}
          {/*          <WarningOutlined style={{ marginRight: '10px' }} />*/}
          {/*          After starting Auto-Trader on a session for the first time,*/}
          {/*          the session cannot be switched back to a regular session.*/}
          {/*        </div>*/}
          {/*        <Divider />*/}
          {/*        <br />*/}
          {/*        <div className="autoTraderMenuRow">*/}
          {/*          <div className="autoTraderMenuLabel">Status:</div>*/}
          {/*          <div className="autoTraderMenuValue">*/}
          {/*            {AUTO_TRADING_STATUSES[session.auto_trader_status]}{' '}*/}
          {/*            <Badge*/}
          {/*              status={badgeMapping[session.auto_trader_status].status}*/}
          {/*              style={{ marginLeft: 10 }}*/}
          {/*            />*/}
          {/*            /!* Optionally include your popover here *!/*/}
          {/*          </div>*/}
          {/*        </div>*/}
          {/*        <div className="autoTraderMenuRow">*/}
          {/*          <div className="autoTraderMenuLabel">*/}
          {/*            Auto-traded players:*/}
          {/*          </div>*/}
          {/*          <div className="autoTraderMenuValue">*/}
          {/*            {session.auto_traded_players &&*/}
          {/*            session.auto_traded_players.length !== 0 ? (*/}
          {/*              <div style={{ display: 'inline-block' }}>*/}
          {/*                /!*  {session.auto_traded_players.map(player_id: number => {*!/*/}
          {/*                /!*    return players.find(item => item.id === player_id);*!/*/}
          {/*                /!*  })*!/*/}
          {/*                /!*}*!/*/}
          {/*                {session.auto_traded_players.map(player_id => {*/}
          {/*                  const player = players.find(*/}
          {/*                    player => player.id === player_id*/}
          {/*                  );*/}
          {/*                  if (player) return `${player.name}, `;*/}
          {/*                })}*/}
          {/*                /!*tpl1, tpl2*!/*/}
          {/*              </div>*/}
          {/*            ) : (*/}
          {/*              <div style={{ display: 'inline-block' }}>*/}
          {/*                No players selected, tick them using the table and*/}
          {/*                save.*/}
          {/*              </div>*/}
          {/*            )}*/}
          {/*            <Tooltip title={disabled ? tooltipMessage : ''}>*/}
          {/*              <span>*/}
          {/*                <Button*/}
          {/*                  size="middle"*/}
          {/*                  disabled={disabled}*/}
          {/*                  style={{ marginLeft: 10 }}*/}
          {/*                  onClick={() =>*/}
          {/*                    dispatch(*/}
          {/*                      updateAutoTradedPlayers({*/}
          {/*                        session_id: session_id,*/}
          {/*                        player_ids: selectedRowKeys.map(key =>*/}
          {/*                          Number(key)*/}
          {/*                        ),*/}
          {/*                      })*/}
          {/*                    )*/}
          {/*                  }*/}
          {/*                >*/}
          {/*                  Save*/}
          {/*                </Button>*/}
          {/*              </span>*/}
          {/*            </Tooltip>*/}
          {/*          </div>*/}
          {/*        </div>*/}
          {/*        /!*<div>*!/*/}
          {/*        <div style={{ display: 'inline-block' }}>*/}
          {/*          Auto-trade from{' '}*/}
          {/*          {formatters.dateOnly(progressFromDate || '')}{' '}*/}
          {/*          {additionalText} until*/}
          {/*        </div>*/}
          {/*        <DatePicker*/}
          {/*          style={{ marginLeft: 10 }}*/}
          {/*          value={selectedDate}*/}
          {/*          onChange={handleDateChange}*/}
          {/*          disabled={disabled}*/}
          {/*        />*/}
          {/*        <Button*/}
          {/*          size="middle"*/}
          {/*          disabled={selectedDate === null || disabled}*/}
          {/*          style={{ marginLeft: 10 }}*/}
          {/*          onClick={() =>*/}
          {/*            dispatch(*/}
          {/*              updateAutoTradeUntilDate({*/}
          {/*                session_id: session_id,*/}
          {/*                date: selectedDate,*/}
          {/*              })*/}
          {/*            )*/}
          {/*          }*/}
          {/*        >*/}
          {/*          Save*/}
          {/*        </Button>*/}
          {/*        <br />*/}
          {/*        <br />*/}
          {/*        {session.auto_trader_status === NEVER_AUTO_TRADED && (*/}
          {/*          <div>*/}
          {/*            <Button*/}
          {/*              type={'primary'}*/}
          {/*              loading={waiting}*/}
          {/*              onClick={() => {*/}
          {/*                dispatch(startAutoTrade({ session_id: session_id }));*/}
          {/*              }}*/}
          {/*            >*/}
          {/*              Start Auto-Trader*/}
          {/*            </Button>*/}
          {/*            /!*<Button*!/*/}
          {/*            /!*  style={{ marginLeft: 10 }}*!/*/}
          {/*            /!*  // type={'primary'}*!/*/}
          {/*            /!*  onClick={() => {*!/*/}
          {/*            /!*    // dispatch(cloneSession({ session_id: session_id }));*!/*/}
          {/*            /!*  }}*!/*/}
          {/*            /!*>*!/*/}
          {/*            /!*  Clone this session*!/*/}
          {/*            /!*</Button>*!/*/}
          {/*          </div>*/}
          {/*        )}*/}
          {/*        {session.auto_trader_status === AUTO_TRADING && (*/}
          {/*          <Button*/}
          {/*            type={'primary'}*/}
          {/*            onClick={() => {*/}
          {/*              dispatch(startAutoTrade({ session_id: session_id }));*/}
          {/*            }}*/}
          {/*          >*/}
          {/*            Pause Auto-Trader*/}
          {/*          </Button>*/}
          {/*        )}*/}
          {/*        {session.auto_trader_status === AT_PAUSED && (*/}
          {/*          <Button*/}
          {/*            type={'primary'}*/}
          {/*            onClick={() => {*/}
          {/*              dispatch(startAutoTrade({ session_id: session_id }));*/}
          {/*            }}*/}
          {/*          >*/}
          {/*            Resume Auto-Trader*/}
          {/*          </Button>*/}
          {/*        )}*/}
          {/*      </div>*/}
          {/*    )}*/}
          {/*  </Card>*/}
          {/*</Row>*/}
        </Col>
        <Col
          span={8}
          style={{
            borderLeft: '2px solid #eeeeee',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Row>
            <Card title={'Settings'} style={{ width: '100%' }}>
              {showLegacyAnalysisTabToggle && (
                <div className="autoTraderMenuRow">
                  <div className="autoTraderMenuLabel">Show Analysis tab:</div>
                  <div className="autoTraderMenuValue">
                    <Tooltip title={analysisTabTooltip}>
                      <span>
                        <Checkbox
                          disabled={analysisTabToggleDisabled}
                          checked={session.show_analysis_tab}
                          onChange={e => {
                            dispatch(
                              toggleShowAnalysisTab({
                                session_id: session.id,
                                show_analysis_tab: e.target.checked,
                              })
                            );
                          }}
                        />
                      </span>
                    </Tooltip>
                  </div>
                </div>
              )}
              <div className="autoTraderMenuRow">
                <div className="autoTraderMenuLabel">
                  Show Analysis Studio tab:
                </div>
                <div className="autoTraderMenuValue">
                  <Checkbox
                    checked={session.show_analysis_studio_tab}
                    onChange={e => {
                      dispatch(
                        toggleShowAnalysisStudioTab({
                          session_id: session.id,
                          show_analysis_studio_tab: e.target.checked,
                        })
                      );
                    }}
                  />
                </div>
              </div>
              <div className="autoTraderMenuRow">
                <div className="autoTraderMenuLabel">
                  Show bias &amp; final report in player view:
                </div>
                <div className="autoTraderMenuValue">
                  <Checkbox
                    checked={session.show_bias_and_report_in_player_view}
                    onChange={e => {
                      dispatch(
                        toggleShowBiasAndReportInPlayerView({
                          session_id: session.id,
                          show_bias_and_report_in_player_view: e.target.checked,
                        })
                      );
                    }}
                  />
                </div>
              </div>
              <div className="autoTraderMenuRow">
                <div className="autoTraderMenuLabel">News grouping:</div>
                <div className="autoTraderMenuValue">
                  <Select<'all' | 'geography' | 'sector'>
                    value={session.news_grouping_mode}
                    style={{ minWidth: 160 }}
                    options={newsGroupingOptions}
                    onChange={value => {
                      dispatch(
                        updateNewsGroupingMode({
                          session_id: session.id,
                          news_grouping_mode: value,
                        })
                      );
                    }}
                  />
                </div>
              </div>
              {session.scenario_type === HISTORICAL_SCENARIO && (
                <>
                  <div className="autoTraderMenuRow">
                    <div className="autoTraderMenuLabel">
                      Stagger news delivery:
                    </div>
                    <div className="autoTraderMenuValue">
                      <Tooltip title="Changes apply from the next historical period.">
                        <Checkbox
                          checked={staggeredNewsEnabled}
                          onChange={e => {
                            const enabled = e.target.checked;
                            setStaggeredNewsEnabled(enabled);
                            dispatch(
                              updateHistoricalNewsDelivery({
                                session_id: session.id,
                                historical_news_delivery_enabled: enabled,
                                historical_news_push_interval_seconds:
                                  newsPushInterval ?? 30,
                                historical_news_quiet_seconds:
                                  newsQuietSeconds ?? 0,
                              }),
                            );
                          }}
                        />
                      </Tooltip>
                    </div>
                  </div>
                  <div className="autoTraderMenuRow">
                    <div className="autoTraderMenuLabel">
                      Seconds between news pushes:
                    </div>
                    <div className="autoTraderMenuValue">
                      <InputNumber
                        value={newsPushInterval}
                        min={2}
                        disabled={!staggeredNewsEnabled}
                        onChange={value =>
                          setNewsPushInterval(value ?? undefined)
                        }
                      />
                    </div>
                  </div>
                  <div className="autoTraderMenuRow">
                    <div className="autoTraderMenuLabel">
                      Final news-free seconds:
                    </div>
                    <div className="autoTraderMenuValue">
                      <InputNumber
                        value={newsQuietSeconds}
                        min={0}
                        max={Math.max(0, session.historical_sleep_time - 1)}
                        disabled={!staggeredNewsEnabled}
                        onChange={value =>
                          setNewsQuietSeconds(value ?? undefined)
                        }
                      />
                      <Button
                        size="middle"
                        disabled={
                          !staggeredNewsEnabled ||
                          newsPushInterval === undefined ||
                          newsQuietSeconds === undefined
                        }
                        style={{ marginLeft: 10 }}
                        onClick={() =>
                          dispatch(
                            updateHistoricalNewsDelivery({
                              session_id: session.id,
                              historical_news_delivery_enabled:
                                staggeredNewsEnabled,
                              historical_news_push_interval_seconds:
                                newsPushInterval ?? 30,
                              historical_news_quiet_seconds:
                                newsQuietSeconds ?? 0,
                            }),
                          )
                        }
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default Players;
